import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToastOptional } from '../../context/ToastContext';
import {
  type ProjectMilestone,
  type ProjectMilestoneStatus,
  getProjectMilestones,
  getPublicProjectMilestones,
  createProjectMilestone,
  updateProjectMilestone,
  deleteProjectMilestone,
} from '../../api/projects';

interface ProjectMilestonesSectionProps {
  projectId: string;
  /** When true, uses public API and shows list only (no add/edit/delete). For normal user frontend. */
  readOnly?: boolean;
  /** When false, list loads with auth but add/edit/delete are hidden (view-only dashboard). Ignored when readOnly is true. */
  allowMutations?: boolean;
}

const STATUS_OPTIONS: ProjectMilestoneStatus[] = ['Planned', 'InProgress', 'Done'];

const ProjectMilestonesSection: React.FC<ProjectMilestonesSectionProps> = ({
  projectId,
  readOnly = false,
  allowMutations = true,
}) => {
  const toast = useToastOptional();
  const canMutate = !readOnly && allowMutations;
  const [milestones, setMilestones] = useState<ProjectMilestone[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ title: '', dueDate: '', description: '' });
  const [addError, setAddError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const list = readOnly
        ? await getPublicProjectMilestones(projectId)
        : await getProjectMilestones(projectId);
      setMilestones(list);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load milestones', err);
      setError(readOnly ? 'Unable to load milestones.' : 'Unable to load milestones for this project.');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, readOnly]);

  useEffect(() => {
    if (projectId) {
      load();
    }
  }, [projectId, load]);

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.title.trim()) {
      setAddError('Title is required.');
      return;
    }
    setAddError(null);
    setIsLoading(true);
    try {
      await createProjectMilestone(projectId, {
        title: addForm.title.trim(),
        description: addForm.description.trim() || null,
        dueDate: addForm.dueDate.trim() || null,
        status: 'Planned',
        order: milestones.length,
      });
      setAddForm({ title: '', dueDate: '', description: '' });
      setShowAddModal(false);
      await load();
      toast?.showToast('Milestone added.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to create milestone', err);
      setAddError('Failed to add milestone. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddForm({ title: '', dueDate: '', description: '' });
    setAddError(null);
  };

  const handleStatusChange = async (m: ProjectMilestone, status: ProjectMilestoneStatus) => {
    setIsLoading(true);
    setError(null);
    try {
      await updateProjectMilestone(projectId, m.id, { status });
      await load();
      toast?.showToast('Milestone updated.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to update milestone', err);
      setError('Failed to update milestone.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (m: ProjectMilestone) => {
    if (!window.confirm(`Delete milestone "${m.title}"?`)) return;
    setIsLoading(true);
    setError(null);
    try {
      await deleteProjectMilestone(projectId, m.id);
      await load();
      toast?.showToast('Milestone removed.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete milestone', err);
      setError('Failed to delete milestone.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-3 border-top pt-3">
      <h6 className="mb-2">
        <i className="bi bi-list-check me-2" />
        Milestones
      </h6>
      {error && (
        <div className="alert alert-danger py-2" role="alert">
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
        </div>
      )}
      {canMutate && (
        <div className="mb-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => setShowAddModal(true)}
            disabled={isLoading}
          >
            <i className="bi bi-plus-circle me-1" />
            Add milestone
          </button>
        </div>
      )}

      {canMutate && showAddModal && createPortal(
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-list-check me-2" />
                  Add milestone
                </h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeAddModal} disabled={isLoading} />
              </div>
              <form onSubmit={handleAddSubmit}>
                <div className="modal-body">
                  {addError && (
                    <div className="alert alert-danger py-2 mb-2" role="alert">
                      {addError}
                    </div>
                  )}
                  <div className="mb-3">
                    <label htmlFor="milestone-title" className="form-label">Title *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="milestone-title"
                      value={addForm.title}
                      onChange={(e) => setAddForm((f) => ({ ...f, title: e.target.value }))}
                      placeholder="e.g. Phase 1 complete"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="milestone-dueDate" className="form-label">
                      <i className="bi bi-calendar-event me-1" />
                      Due date (optional)
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="milestone-dueDate"
                      value={addForm.dueDate}
                      onChange={(e) => setAddForm((f) => ({ ...f, dueDate: e.target.value }))}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-0">
                    <label htmlFor="milestone-description" className="form-label">
                      Description (optional)
                    </label>
                    <textarea
                      className="form-control"
                      id="milestone-description"
                      rows={3}
                      value={addForm.description}
                      onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder="Add any details…"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={closeAddModal} disabled={isLoading}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        Adding…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-plus-circle me-1" />
                        Add milestone
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}
      {isLoading && (
        <div className="text-muted small mb-2">
          <span className="spinner-border spinner-border-sm me-1" role="status" />
          {readOnly ? 'Loading…' : 'Saving…'}
        </div>
      )}
      {milestones.length === 0 ? (
        <p className="text-muted small mb-0">No milestones defined yet.</p>
      ) : (
        <div className="milestone-timeline">
          <div className="milestone-timeline__line" aria-hidden="true" />
          {milestones.map((m, index) => {
            const isRight = index % 2 === 1;
            const card = (
              <div className="milestone-timeline__card">
                <div className="milestone-timeline__card-title">{m.title}</div>
                <div className="milestone-timeline__card-meta">
                  {m.dueDate
                    ? `Due ${new Date(m.dueDate).toLocaleDateString('en-ZA', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}`
                    : 'No due date'}
                  {(readOnly || !allowMutations) && (
                    <>
                      {' · '}
                      <span className="badge bg-secondary bg-opacity-75">{m.status === 'InProgress' ? 'In Progress' : m.status}</span>
                    </>
                  )}
                </div>
                {m.description && (
                  <p className="milestone-timeline__card-description">{m.description}</p>
                )}
                {canMutate && (
                  <div className="milestone-timeline__card-actions">
                    <select
                      className="form-select form-select-sm"
                      style={{ maxWidth: '10rem' }}
                      value={m.status}
                      onChange={(e) =>
                        handleStatusChange(m, e.target.value as ProjectMilestoneStatus)
                      }
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s === 'InProgress' ? 'In Progress' : s}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleDelete(m)}
                      aria-label={`Delete ${m.title}`}
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                )}
              </div>
            );
            return (
              <div
                key={m.id}
                className={`milestone-timeline__item ${isRight ? 'milestone-timeline__item--right' : 'milestone-timeline__item--left'}`}
              >
                <div className="milestone-timeline__item-side milestone-timeline__item-side--left">
                  {!isRight ? card : null}
                </div>
                <div className="milestone-timeline__dot-wrap">
                  <span
                    className={`milestone-timeline__dot milestone-timeline__dot--${m.status === 'Done' ? 'done' : m.status === 'InProgress' ? 'inprogress' : 'planned'}`}
                    aria-hidden="true"
                  />
                </div>
                <div className="milestone-timeline__item-side milestone-timeline__item-side--right">
                  {isRight ? card : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectMilestonesSection;

